// src/utils/strategyTransform.js

/**
 * Transform frontend strategy data to backend API format
 */
export const transformStrategyForAPI = (frontendData) => {
  // Convert timing object to total minutes
  const convertTimingToMinutes = (timing) => {
    if (!timing) return 0;
    const minutes = timing.min_minutes || 0;
    const seconds = timing.min_seconds || 0;
    return minutes + (seconds / 60);
  };

  const convertTimingToMaxMinutes = (timing) => {
    if (!timing) return 0;
    const minutes = timing.max_minutes || 0;
    const seconds = timing.max_seconds || 0;
    return minutes + (seconds / 60);
  };

  // Transform conditions
  const transformedConditions = (frontendData.conditions || []).map((condition, index) => {
    const baseCondition = {
      name: condition.name || `c${index + 1}`,
      enabled: condition.enabled !== false,
      type: condition.type,
    };

    if (condition.type === 'simultaneous') {
      // For simultaneous, use signal1 and signal2 as objects
      baseCondition.signal1 = condition.signals?.[0] || { algo: '', direction: 'LONG' };
      baseCondition.signal2 = condition.signals?.[1] || { algo: '', direction: 'LONG' };
    } else if (condition.type === 'time_bound') {
      // Time bound needs trigger, target as objects, and timing
      baseCondition.trigger = condition.trigger || { algo: '', direction: 'LONG' };
      baseCondition.target = condition.target || { algo: '', direction: 'LONG' };
      baseCondition.min_minutes = convertTimingToMinutes(condition.timing);
      baseCondition.max_minutes = convertTimingToMaxMinutes(condition.timing);
    } else if (condition.type === 'interference') {
      // Interference needs trigger and target as objects
      baseCondition.trigger = condition.trigger || { algo: '', direction: 'LONG' };
      baseCondition.target = condition.target || { algo: '', direction: 'LONG' };
    }

    return baseCondition;
  });

  // Get confirmation condition name for alert_on
  const confirmationConditionName = frontendData.confirmation_condition_index !== null &&
    frontendData.confirmation_condition_index !== undefined &&
    frontendData.conditions[frontendData.confirmation_condition_index]
    ? frontendData.conditions[frontendData.confirmation_condition_index].name || `c${frontendData.confirmation_condition_index + 1}`
    : transformedConditions[0]?.name || 'c1';

  // Build the API payload - matching exact backend format
  return {
    strategy_id: frontendData.strategy_id,
    symbol: frontendData.symbol || 'BTCUSD',
    timeframe: frontendData.timeframe || '1m',
    conditions: transformedConditions,
    entry_logic: frontendData.entry_logic || 'all',
    alert_config: {
      alert_on: [confirmationConditionName],
      alert_on_entry: frontendData.alert_config?.alert_on_entry !== false,
      individual_alerts: frontendData.alert_config?.individual_alerts || false,
    },
  };
};

/**
 * Transform backend API data to frontend format
 */
export const transformStrategyFromAPI = (backendData) => {
  // Convert min_minutes to timing object
  const convertMinutesToTiming = (minMinutes, maxMinutes) => {
    const min_total = minMinutes || 0;
    const max_total = maxMinutes || 0;
    
    return {
      min_minutes: Math.floor(min_total),
      min_seconds: Math.round((min_total % 1) * 60),
      max_minutes: Math.floor(max_total),
      max_seconds: Math.round((max_total % 1) * 60),
    };
  };

  // Transform conditions
  const transformedConditions = (backendData.conditions || []).map((condition, index) => {
    const baseCondition = {
      id: Date.now() + index,
      name: condition.name || `Condition ${index + 1}`,
      enabled: condition.enabled !== false,
      type: condition.type,
      signals: [],
      trigger: null,
      target: null,
    };

    if (condition.type === 'simultaneous') {
      // Backend sends signal1 and signal2 as objects
      baseCondition.signals = [
        condition.signal1,
        condition.signal2,
      ].filter(Boolean);
    } else if (condition.type === 'time_bound' || condition.type === 'interference') {
      baseCondition.trigger = condition.trigger;
      baseCondition.target = condition.target;
      
      if (condition.type === 'time_bound') {
        baseCondition.timing = convertMinutesToTiming(
          condition.min_minutes,
          condition.max_minutes
        );
      }
    }

    return baseCondition;
  });

  // Find confirmation condition index from alert_on
  const alertOn = backendData.alert_config?.alert_on || [];
  const confirmationName = alertOn[0];
  const confirmationIndex = transformedConditions.findIndex(
    c => c.name === confirmationName
  );

  // Build expected_signals from conditions (extract unique signals)
  const signalMap = new Map();
  transformedConditions.forEach(condition => {
    if (condition.type === 'simultaneous') {
      condition.signals.forEach(signal => {
        if (signal && signal.algo) {
          const key = `${signal.algo}:${signal.direction}`;
          signalMap.set(key, {
            id: Date.now() + Math.random(),
            algo: signal.algo,
            direction: signal.direction,
            description: '',
          });
        }
      });
    } else {
      [condition.trigger, condition.target].forEach(signal => {
        if (signal && signal.algo) {
          const key = `${signal.algo}:${signal.direction}`;
          signalMap.set(key, {
            id: Date.now() + Math.random(),
            algo: signal.algo,
            direction: signal.direction,
            description: '',
          });
        }
      });
    }
  });

  return {
    strategy_id: backendData.strategy_id,
    strategy_name: backendData.strategy_id,
    symbol: backendData.symbol || '',
    timeframe: backendData.timeframe || '1m',
    description: '',
    expected_signals: Array.from(signalMap.values()),
    conditions: transformedConditions,
    confirmation_condition_index: confirmationIndex >= 0 ? confirmationIndex : null,
    entry_logic: backendData.entry_logic || 'all',
    webhook_url: backendData.webhook_url || '',
    alert_config: backendData.alert_config || {
      alert_on: [],
      alert_on_entry: true,
      individual_alerts: false,
    },
  };
};

/**
 * Validate strategy data before submission
 */
export const validateStrategy = (data) => {
  const errors = [];
  
  if (!data.strategy_id || !data.strategy_id.trim()) {
    errors.push('Strategy ID is required');
  }
  
  if (!data.symbol || !data.symbol.trim()) {
    errors.push('Symbol is required');
  }
  
  if (!data.timeframe) {
    errors.push('Timeframe is required');
  }
  
  const signals = data.expected_signals || [];
  const conditions = data.conditions || [];
  
  if (signals.length < 2) {
    errors.push('At least 2 signals are required');
  }
  
  if (conditions.length === 0) {
    errors.push('At least one condition is required');
  }
  
  // Validate each condition
  conditions.forEach((condition, index) => {
    const conditionName = condition.name?.trim() || `Condition ${index + 1}`;
    
    if (!condition.name || !condition.name.trim()) {
      errors.push(`Condition ${index + 1}: Must have a name`);
    }
    
    if (!condition.type) {
      errors.push(`${conditionName}: Must select a condition type (Time Bound, Simultaneous, or Interference)`);
      return;
    }
    
    if (condition.type === 'simultaneous') {
      if (!condition.signals || condition.signals.length < 2) {
        errors.push(`${conditionName}: Simultaneous type requires at least 2 signals`);
      } else {
        condition.signals.forEach((signal, sigIdx) => {
          if (!signal || !signal.algo || !signal.direction) {
            errors.push(`${conditionName}: Signal ${sigIdx + 1} is incomplete (needs algo and direction)`);
          }
        });
      }
    }
    
    if (condition.type === 'time_bound' || condition.type === 'interference') {
      if (!condition.trigger) {
        errors.push(`${conditionName}: Missing trigger signal - please select a trigger`);
      } else if (!condition.trigger.algo || !condition.trigger.direction) {
        errors.push(`${conditionName}: Trigger signal is incomplete (needs algo and direction)`);
      }
      
      if (!condition.target) {
        errors.push(`${conditionName}: Missing target signal - please select a target`);
      } else if (!condition.target.algo || !condition.target.direction) {
        errors.push(`${conditionName}: Target signal is incomplete (needs algo and direction)`);
      }
    }
  });
  
  if (data.confirmation_condition_index === null || data.confirmation_condition_index === undefined) {
    errors.push('Confirmation condition must be selected');
  } else if (data.confirmation_condition_index >= conditions.length) {
    errors.push('Selected confirmation condition does not exist');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
};
